import { Typography } from '@mui/material';
import { useEffect, useRef } from 'react';

import { FlowChapterBubble } from './FlowChapterBubble';
import { chapterStateOf, chaptersOf, type FlowStep } from './grouping';

interface FlowProgressProps {
  steps: FlowStep[];
  index: number;
  outcomes: Map<string, 'done' | 'skipped'>;
}

/**
 * Where the director is in the run, told as a ledger of companies.
 *
 * The obligation being discharged belongs to a *company*, so the queue is drawn
 * as one bubble per company rather than one segment per step. A flat rail of 32
 * identical slivers — which is what a busy portfolio produces — says only "many
 * left"; a director wants to know whose work is on screen, how much of it is
 * left, and when a company is finally clear of them.
 *
 * Each bubble carries its own documents as pips, so the unit of progress the
 * director feels is the company, not the step. When the last of a company's
 * documents resolves the bubble seals: it collapses to its name under a tick
 * and rules itself off, the way a settled line in a ledger does. That moment is
 * the point of the component, and everything else stays deliberately quiet so
 * it is the thing that reads.
 *
 * A skipped step never seals a company. It closes as `partial` — struck through
 * rather than ticked — because the company still owes something, and a tick
 * there would be a lie the director discovers back on their list.
 */
export const FlowProgress = ({ steps, index, outcomes }: FlowProgressProps) => {
  const chapters = chaptersOf(steps);
  const position = Math.min(index + 1, steps.length);

  const activeRef = useRef<HTMLLIElement>(null);

  // A portfolio of six companies overflows the dialog's width, so the rail
  // scrolls and the company in hand is kept in view. Without this the director
  // walks off the right edge of a rail that never follows them.
  useEffect(() => {
    activeRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    });
  }, [index]);

  const activeChapter = chapters.find(
    (chapter) => chapterStateOf(chapter, index, outcomes) === 'active',
  );
  const remaining =
    activeChapter === undefined
      ? 0
      : activeChapter.steps.filter((step) => !outcomes.has(step.id)).length;

  return (
    <div className="flex flex-col gap-2.5">
      <ol
        // The rail is decoration over facts stated in words below it, and a
        // horizontal scroller full of company names is a tab stop that leads
        // nowhere (§11).
        className="-mx-1 flex scrollbar-none items-stretch gap-1.5 overflow-x-auto px-1 pb-1"
        aria-hidden
      >
        {chapters.map((chapter) => {
          const state = chapterStateOf(chapter, index, outcomes);

          return (
            <li
              // Keyed by where the chapter STARTS, not by its company. One
              // company can open two chapters — the queue is in urgency order,
              // so another company's work can sit between two runs of theirs,
              // and `chaptersOf` deliberately does not merge them. Keying by
              // company id then gives two siblings the same key: React reuses
              // the wrong element, and `activeRef` lands on the wrong bubble.
              // `startIndex` is unique by construction.
              key={chapter.startIndex}
              ref={state === 'active' ? activeRef : undefined}
              className="shrink-0"
            >
              <FlowChapterBubble
                chapter={chapter}
                state={state}
                index={index}
                outcomes={outcomes}
              />
            </li>
          );
        })}
      </ol>

      {/* The rail carries its meaning in colour and position, so the same facts
          are stated in words — and this is the live region, not the rail. */}
      <Typography variant="caption" color="text.secondary" role="status">
        Step {position} of {steps.length}
        {activeChapter !== undefined && remaining > 0 && (
          <>
            {' · '}
            {remaining === 1 ? '1 left for ' : `${String(remaining)} left for `}
            {activeChapter.companyName}
          </>
        )}
      </Typography>
    </div>
  );
};
